import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWebsiteSettings } from '../../context/WebsiteSettingsContext';
import './StaticPages.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface ValueItem { emoji: string; title: string; title_bn?: string; description: string; description_bn?: string; }

interface AboutData {
  hero_title: string; hero_title_bn?: string;
  hero_subtitle: string; hero_subtitle_bn?: string;
  story_title: string; story_title_bn?: string;
  story_content: string; story_content_bn?: string;
  values: ValueItem[];
  mission_title: string; mission_title_bn?: string;
  mission_content: string; mission_content_bn?: string;
  cta_title: string; cta_title_bn?: string;
  cta_subtitle: string; cta_subtitle_bn?: string;
}

const AboutUs: React.FC = () => {
  const { settings } = useWebsiteSettings();
  const { i18n } = useTranslation();
  const isBn = i18n.language === 'bn';
  const siteName = settings?.site_name || 'Our Store';
  const [data, setData] = useState<AboutData | null>(null);

  useEffect(() => {
    fetch(`${API}/pages/about/`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setData(d))
      .catch(() => {});
  }, []);

  const t = (en: string | undefined, bn: string | undefined, fallback: string) =>
    isBn ? (bn || en || fallback) : (en || fallback);

  const hero_title = t(data?.hero_title, data?.hero_title_bn, 'About Us');
  const hero_subtitle = t(data?.hero_subtitle, data?.hero_subtitle_bn, "We're passionate about delivering quality products with a seamless shopping experience.");
  const story_title = t(data?.story_title, data?.story_title_bn, 'Who We Are');
  const story_content = t(data?.story_content, data?.story_content_bn, `Welcome to ${siteName} \u2014 a destination for premium quality products curated with care.`);
  const values = data?.values || [
    { emoji: '\uD83C\uDFC6', title: 'Quality First', description: 'Every product is carefully vetted to meet our high standards before reaching you.' },
    { emoji: '\uD83D\uDE80', title: 'Fast Delivery', description: 'We partner with reliable couriers to ensure your orders arrive quickly and safely.' },
    { emoji: '\uD83D\uDCAC', title: 'Customer Care', description: 'Our support team is always ready to help you with any question or concern.' },
    { emoji: '\uD83D\uDD12', title: 'Secure Shopping', description: 'Shop confidently \u2014 your payments and personal data are always protected.' },
  ];
  const mission_title = t(data?.mission_title, data?.mission_title_bn, 'Our Mission');
  const mission_content = t(data?.mission_content, data?.mission_content_bn, 'Our mission is to build a trusted e-commerce platform where customers can discover unique, high-quality products.');
  const cta_title = t(data?.cta_title, data?.cta_title_bn, 'Have Questions?');
  const cta_subtitle = t(data?.cta_subtitle, data?.cta_subtitle_bn, "We'd love to hear from you.");

  return (
    <div className="static-page">
      <div className="static-hero">
        <h1>{hero_title}</h1>
        <p>{hero_subtitle}</p>
      </div>

      <div className="static-content">
        <div className="static-section">
          <span className="section-tag">{isBn ? '\u0986\u09AE\u09BE\u09A6\u09C7\u09B0 \u0995\u09BE\u09B9\u09BF\u09A8\u09C0' : 'Our Story'}</span>
          <h2>{story_title}</h2>
          {story_content.split('\n').filter(Boolean).map((p, i) => (
            <p key={i} style={i > 0 ? { marginTop: '14px' } : undefined}>{p}</p>
          ))}
        </div>

        <hr className="static-divider" />

        <div className="static-section">
          <span className="section-tag">{isBn ? '\u0986\u09AE\u09B0\u09BE \u09AF\u09BE \u09AC\u09BF\u09B6\u09CD\u09AC\u09BE\u09B8 \u0995\u09B0\u09BF' : 'What We Stand For'}</span>
          <h2>{isBn ? '\u0986\u09AE\u09BE\u09A6\u09C7\u09B0 \u09AE\u09C2\u09B2\u09CD\u09AF\u09AC\u09CB\u09A7' : 'Our Values'}</h2>
          <div className="info-grid">
            {values.map((v, i) => (
              <div className="static-info-card" key={i}>
                <div className="static-info-card-icon">{v.emoji}</div>
                <h4>{isBn ? (v.title_bn || v.title) : v.title}</h4>
                <p>{isBn ? (v.description_bn || v.description) : v.description}</p>
              </div>
            ))}
          </div>
        </div>

        <hr className="static-divider" />

        <div className="static-section">
          <span className="section-tag">{isBn ? '\u09AE\u09BF\u09B6\u09A8' : 'Mission'}</span>
          <h2>{mission_title}</h2>
          <p>{mission_content}</p>
          <div className="static-card" style={{ marginTop: '24px' }}>
            <h3>{isBn ? '\u0995\u09C7\u09A8\u09BE\u0995\u09BE\u099F\u09BE \u09B6\u09C1\u09B0\u09C1 \u0995\u09B0\u09C1\u09A8?' : 'Ready to shop?'}</h3>
            <p>
              {isBn ? '\u0986\u09AE\u09BE\u09A6\u09C7\u09B0 \u09AA\u09A3\u09CD\u09AF \u09A6\u09C7\u0996\u09C1\u09A8' : 'Explore our full range of products and find something you\'ll love.'}{' '}
              <Link to="/products" style={{ color: 'var(--primary)', fontWeight: 600 }}>{isBn ? '\u09B8\u09CD\u099F\u09CB\u09B0 \u09AC\u09CD\u09B0\u09BE\u0989\u099C \u0995\u09B0\u09C1\u09A8 \u2192' : 'Browse the store \u2192'}</Link>
            </p>
          </div>
        </div>

        <hr className="static-divider" />

        <div className="static-section" style={{ textAlign: 'center' }}>
          <span className="section-tag">{isBn ? '\u09AF\u09CB\u0997\u09BE\u09AF\u09CB\u0997' : 'Get In Touch'}</span>
          <h2 style={{ marginTop: 4 }}>{cta_title}</h2>
          <p style={{ maxWidth: 480, margin: '0 auto 20px' }}>{cta_subtitle}</p>
          <Link to="/contact" style={{ display: 'inline-block', padding: '12px 32px', background: 'var(--primary)', color: '#fff', borderRadius: 'var(--radius-sm)', fontWeight: 600, fontSize: '0.97rem', textDecoration: 'none', transition: 'background var(--transition)' }}>
            {isBn ? '\u09AF\u09CB\u0997\u09BE\u09AF\u09CB\u0997 \u0995\u09B0\u09C1\u09A8' : 'Contact Us'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;
